// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Star from "@insite/mobius/Icons/Star";
import StarX from "@insite/mobius/Icons/StarX";
import Tag from "@insite/mobius/Icons/Tag";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import ProductAddedToCartMessage from "@werner/Overrides/Components/ProductAddedToCartMessage";
import { productAddToCartButtonStyles } from "@werner/Overrides/Components/ProductAddToCartButton";
import Exit from "@werner/XDL/Icons/Exit";
import Trash from "@werner/XDL/Icons/Trash";
import { color } from "@werner/XDL/Utilities";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
    toggleFavorite?: () => void;
    toggleHide?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionHideClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionHideLink?: LinkPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    actionFavoriteClickable?: ClickablePresentationProps;
    basicIconGridContainer?: GridContainerProps;
    detailIconGridItem?: GridItemProps;
    starIcon?: IconPresentationProps;
    starXIcon?: IconPresentationProps;
    tagTooltip?: TooltipPresentationProps;
    // XNG-Changes
    gridItem?: GridItemProps;
}

export const wishListCardStyles: WishListCardStyles = {
    gridContainer: {
        gap: 5,
    },
    wishListInfoGridItem: {
        width: [10, 10, 8, 10, 10],
        align: "middle",
    },
    basicInfoGridItem: { width: [12, 12, 5, 4, 4] },
    basicInfoGridContainer: {
        gap: 10,
        css: css`
            margin-left: 10px;
        `,
    },
    detailLinkGridItem: { width: 12 },
    descriptionGridItem: { width: 12 },
    descriptionText: { ellipsis: true },
    lastUpdatedGridItem: { width: 12 },
    extendedInfoGridItem: {
        width: [12, 12, 7, 8, 8],
        align: "middle",
    },
    productImageLink: {
        css: css`
            margin: 0 15px;
        `,
    },
    productImagesGridItem: {
        width: [12, 12, 12, 8, 8],
        css: css`
            flex-wrap: wrap;
        `,
    },
    productImage: {
        css: css`
            width: 75px;
            height: 75px;
            img {
                max-width: 100%;
                max-height: 100%;
                height: auto;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    sharingStatusGridItem: { width: 12 },
    actionGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    actionGridMediumHidden: {
        css: css`
            width: 100%;
        `,
    },
    actionAddToCartButton: {
        ...productAddToCartButtonStyles,
    },
    actionHideLink: {
        css: css`
            display: block;
            margin: 10px auto 0 auto;
        `,
    },
    actionDeleteButton: {
        variant: "secondary",
        icon: {
            src: Trash,
            position: "right",
            ...{ color: "primary", size: 26 },
        },
        css: css`
            justify-content: space-between;
            &:hover {
                ${IconWrapper} {
                    svg {
                        color: ${color("common.ui3")};
                        fill: ${color("common.ui3")};
                    }
                }
            }
        `,
    },
    actionLeaveButton: {
        variant: "secondary",
        icon: {
            src: Exit,
            position: "right",
            ...{ color: "primary", size: 26 },
        },
        css: css`
            justify-content: space-between;
            &:hover {
                ${IconWrapper} {
                    svg {
                        color: ${color("common.ui3")};
                        fill: ${color("common.ui3")};
                    }
                }
            }
        `,
    },
    sharingStatus: {
        privateTooltip: {
            cssOverrides: {
                tooltipContainer: css`
                    margin-left: -80px;
                    &::after {
                        margin-left: -36px;
                    }
                `,
            },
        },
    },
    gridItem: {
        width: [12, 12, 6, 4, 4],
        css: css`
            flex-direction: column;
            &:not(:last-child):not(:first-child) {
                align-items: center;
            }
            &:last-child {
                gap: 10px;
                align-items: flex-end;
                .buttons-wrapper {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
            }
        `,
    },
    basicIconGridContainer: {
        gap: 10,
        css: css`
            width: 15px;
            flex-shrink: 0;
        `,
        offsetCss: css`
            flex-direction: column;
        `,
    },
    actionFavoriteClickable: {
        css: css`
            width: 15px;
        `,
    },
    starIcon: {
        src: Star,
        size: 15,
    },
    starXIcon: {
        src: StarX,
        size: 15,
    },
    tagTooltip: {
        iconProps: {
            src: Tag,
            size: 15,
            css: css`
                margin: 4px 0;
            `,
        },
    },
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
    toggleFavorite,
    toggleHide,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const isLoadingAnyList = useAppSelector(state => Object.keys(state.data.wishLists.isLoading).length > 0);

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    this.onSuccess?.(resultProps.apiResult);
                } else if (resultProps.errorMessage) {
                    toasterContext.addToast({
                        body: resultProps.errorMessage,
                        messageType: "danger",
                    });
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        const qtyExceededInventory = result?.cartLines?.some(c => (c.qtyOrdered || 0) > c.qtyOnHand);
        const message = result.notAllAddedToCart
            ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
            : translate("List Added to Cart");

        toasterContext.addToast({
            body: (
                <ProductAddedToCartMessage
                    defaultMessageOverride={message as string}
                    qtyExceededInventory={!!qtyExceededInventory}
                    isQtyAdjusted={false}
                    multipleProducts={true}
                />
            ),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    let lastUpdatedDisplay = `${translate("Updated")} ${updatedOnDisplay}`;
    if (wishList.updatedByDisplayName && !wishList.isAutogenerated) {
        lastUpdatedDisplay += ` ${translate("by")} ${wishList.updatedByDisplayName}`;
    }

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const toggleHideButtonText = wishList.isHidden ? translate("Unhide List") : translate("Hide List");
    const toggleFavoriteLabel = wishList.isFavorite
        ? translate("Remove from favorites")
        : translate("Add to favorites");
    const deleteLabel = translate("Delete");
    const leaveLabel = translate("Leave List");
    const [styles] = React.useState(() => mergeToNew(wishListCardStyles, extendedStyles));

    // <XNG-Changes>
    const totalCount = wishList.wishListLinesCount;
    // </XNG-Changes>

    // XNG-Changes: update structure
    return (
        <GridContainer {...styles.gridContainer} data-test-selector="wishListCard">
            {/* // XNG-Change */}
            {/* {!wishList.isAutogenerated && (
                <GridItem {...styles.gridItem}>
                    <Clickable
                        {...styles.actionFavoriteClickable}
                        onClick={toggleFavorite}
                        aria-label={toggleFavoriteLabel}
                        data-test-selector={`myListsFavorite_${wishList.id}`}
                        disabled={isLoadingAnyList}
                    >
                        {wishList.isFavorite && (
                            <Icon
                                {...styles.starXIcon}
                                data-test-selector={`myListsFavoriteStarXIcon_${wishList.id}`}
                            />
                        )}
                        {!wishList.isFavorite && (
                            <Icon {...styles.starIcon} data-test-selector={`myListsFavoriteStarIcon_${wishList.id}`} />
                        )}
                    </Clickable>
                </GridItem>
            )} */}
            {/* {wishList.wishListTags && wishList.wishListTags.length > 0 && (
                <GridItem {...styles.gridItem} data-test-selector={`myListsTags_${wishList.id}`}>
                    <Tooltip
                        {...styles.tagTooltip}
                        text={`${translate("Tags Assigned")}: ${wishList.wishListTags.map(o => o.tag).join(", ")}`}
                    />
                </GridItem>
            )} */}
            <GridItem {...styles.gridItem}>
                <MyListsDetailsPageTypeLink
                    title={wishList.name}
                    wishListId={wishList.id}
                    testSelector="wishListCardName"
                />
                <Typography {...styles.descriptionText} data-test-selector="wishListCardDescription">
                    {wishList.description}
                </Typography>
                <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                    {lastUpdatedDisplay}
                </Typography>
            </GridItem>
            <GridItem {...styles.gridItem} data-test-selector="productImagesGridItem">
                <StyledWrapper>
                    {wishList.wishListLineCollection &&
                        wishList.wishListLineCollection.length > 0 &&
                        wishList.wishListLineCollection.slice(0, 3).map(line => (
                            <Link
                                key={line.id.toString()}
                                {...styles.productImageLink}
                                href={line.productUri}
                                aria-label={`${line.altText}`}
                            >
                                <LazyImage
                                    {...styles.productImage}
                                    src={line.smallImagePath}
                                    altText={line.altText}
                                    title={line.altText}
                                />
                            </Link>
                        ))}
                </StyledWrapper>

                {totalCount ? (
                    <Typography>
                        {translate("Total {0} Item{1}", `${totalCount}`, totalCount > 1 ? "s" : "")}
                    </Typography>
                ) : null}
            </GridItem>
            <GridItem {...styles.gridItem}>
                <StyledWrapper className="buttons-wrapper">
                    <Button
                        {...styles.actionAddToCartButton}
                        disabled={!canAddToCart || isUpdatingCart}
                        data-test-selector={`wishListAddToCartButton_${wishList.id}`}
                        onClick={clickAddToCartHandler}
                        aria-label={`${addToCartLabel}`}
                    >
                        {addToCartLabel}
                    </Button>
                    {!wishList.isAutogenerated && (
                        <Button
                            {...styles.actionDeleteButton}
                            onClick={deleteWishList}
                            data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                            aria-label={`${deleteLabel}`}
                        >
                            {deleteLabel}
                        </Button>
                    )}
                    {/* {allowEditingOfWishLists &&
                        !!deleteWishList &&
                        !wishList.isSharedList &&
                        !wishList.isAutogenerated && (
                            <Button
                                {...styles.actionLeaveButton}
                                onClick={deleteWishList}
                                data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                                aria-label={`${deleteLabel}`}
                            >
                                {toggleHideButtonText}
                            </Button>
                        )} */}
                    {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers && (
                            <Button
                                {...styles.actionLeaveButton}
                                onClick={leaveWishList}
                                data-test-selector={`wishListCardLeaveButton_${wishList.id}`}
                                aria-label={`${leaveLabel}`}
                            >
                                {leaveLabel}
                            </Button>
                        )}
                </StyledWrapper>
                <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
            </GridItem>
        </GridContainer>
    );
};

export default connect(mapStateToProps)(WishListCard);
